import { useEffect, useRef, useState } from 'react';
import { Collapse, Menu, MenuItem, Stack, Switch, Tooltip } from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import type { BoardDetails } from '../../interfaces/BoardDetails';

type OwnerSection = 'visibility' | 'access' | 'board' | 'cards' | 'account';
type OwnerSectionState = Record<OwnerSection, boolean>;
type ParticipantNameVisibilityMode = 'hidden' | 'owner-only' | 'everyone';

type BoardOwnerMenuProps = {
  anchorEl: HTMLElement | null;
  open: boolean;
  isLocked: boolean;
  isDotVotingStyle: boolean;
  votingEnabled: boolean;
  chatEnabled: boolean;
  commentsEnabled: boolean;
  boardDetails: BoardDetails;
  participantsEnabled: boolean;
  participantUploadsEnabled: boolean;
  viewParticipantNames: boolean;
  allViewParticipantNames: boolean;
  showVoterNames: boolean;
  privateVotingEnabled: boolean;
  giphyEnabled: boolean;
  onClose: () => void;
  onSetParticipantNameVisibility: (mode: ParticipantNameVisibilityMode) => void;
  onToggleShowVoteOwners: () => void;
  onTogglePrivateVoting: () => void;
  onToggleLockBoard: () => void;
  onToggleVoting: () => void;
  onToggleCommenting: () => void;
  onToggleChat: () => void;
  onToggleTimer: () => void;
  onToggleParticipants: () => void;
  onToggleParticipantUploads: () => void;
  onToggleGifs: () => void;
  onExportBoard: () => Promise<void>;
  onOpenImportCards: () => void;
  onOpenClearBoard: () => void;
  onOpenSaveTemplate: () => void;
  onOpenFeedback: () => void;
  onLogout: () => void;
  onOpenSetBackground: () => void;
  onOpenSettings: () => void;
};

type OwnerMenuSwitchItemProps = {
  label: string;
  checked: boolean;
  onToggle: () => void;
  sx?: Record<string, unknown>;
};

const OwnerMenuSwitchItem = ({ label, checked, onToggle, sx }: OwnerMenuSwitchItemProps) => (
  <MenuItem sx={{ pl: 4, pr: 1.5, ...sx }} onClick={onToggle}>
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      gap={2}
      sx={{ width: '100%' }}
    >
      <span>{label}</span>
      <Switch
        edge="end"
        checked={checked}
        onClick={(event) => event.stopPropagation()}
        onChange={onToggle}
        slotProps={{ input: { 'aria-label': label, role: 'switch' } }}
      />
    </Stack>
  </MenuItem>
);

const getDefaultOwnerSectionState = (): OwnerSectionState => ({
  visibility: false,
  access: true,
  board: false,
  cards: false,
  account: false,
});

const BoardOwnerMenu = ({
  anchorEl,
  open,
  isLocked,
  isDotVotingStyle,
  votingEnabled,
  chatEnabled,
  commentsEnabled,
  boardDetails,
  participantsEnabled,
  participantUploadsEnabled,
  viewParticipantNames,
  allViewParticipantNames,
  showVoterNames,
  privateVotingEnabled,
  giphyEnabled,
  onClose,
  onSetParticipantNameVisibility,
  onToggleShowVoteOwners,
  onTogglePrivateVoting,
  onToggleLockBoard,
  onToggleVoting,
  onToggleCommenting,
  onToggleChat,
  onToggleTimer,
  onToggleParticipants,
  onToggleParticipantUploads,
  onToggleGifs,
  onExportBoard,
  onOpenImportCards,
  onOpenClearBoard,
  onOpenSaveTemplate,
  onOpenFeedback,
  onLogout,
  onOpenSetBackground,
  onOpenSettings,
}: BoardOwnerMenuProps) => {
  const wasOpenRef = useRef(open);
  const [expandedOwnerSections, setExpandedOwnerSections] = useState<OwnerSectionState>({
    ...getDefaultOwnerSectionState(),
    access: true,
  });
  const [isCardCreatorNamesExpanded, setIsCardCreatorNamesExpanded] = useState(false);
  const [isExportingBoard, setIsExportingBoard] = useState(false);

  const canSaveTemplates = boardDetails.canSaveTemplates === true;
  const participantNameVisibilityMode = allViewParticipantNames
    ? 'everyone'
    : viewParticipantNames
    ? 'owner-only'
    : 'hidden';
  const cardCreatorNamesLabel =
    participantNameVisibilityMode === 'everyone'
      ? 'Everyone can see card creator names'
      : participantNameVisibilityMode === 'owner-only'
      ? 'Only I can see card creator names'
      : 'Hide card creator names';

  const resetMenuState = () => {
    setExpandedOwnerSections(getDefaultOwnerSectionState());
    setIsCardCreatorNamesExpanded(false);
  };

  useEffect(() => {
    if (wasOpenRef.current && !open) {
      resetMenuState();
    }

    wasOpenRef.current = open;
  }, [open]);

  const closeMenu = () => {
    resetMenuState();
    onClose();
  };

  const toggleOwnerSection = (section: OwnerSection) => {
    setExpandedOwnerSections((currentSections) => ({
      ...currentSections,
      [section]: !currentSections[section],
    }));
  };

  const handleExportBoard = async () => {
    if (isExportingBoard) {
      return;
    }

    setIsExportingBoard(true);

    try {
      await onExportBoard();
    } finally {
      setIsExportingBoard(false);
    }
  };

  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={closeMenu}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      PaperProps={{
        sx: {
          mt: 1,
          minWidth: 312,
        },
      }}
    >
      <MenuItem onClick={() => toggleOwnerSection('access')}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ width: '100%' }}
        >
          <span>Functionality</span>
          {expandedOwnerSections.access ? (
            <ExpandLessIcon fontSize="small" />
          ) : (
            <ExpandMoreIcon fontSize="small" />
          )}
        </Stack>
      </MenuItem>
      <Collapse in={expandedOwnerSections.access} timeout="auto" unmountOnExit>
        <OwnerMenuSwitchItem
          label="Timers"
          checked={boardDetails.timerEnabled !== false}
          onToggle={onToggleTimer}
        />
        <OwnerMenuSwitchItem
          label="Adding cards"
          checked={participantUploadsEnabled}
          onToggle={onToggleParticipantUploads}
        />
        {isDotVotingStyle && (
          <OwnerMenuSwitchItem label="Voting" checked={votingEnabled} onToggle={onToggleVoting} />
        )}
        <OwnerMenuSwitchItem
          label="Commenting"
          checked={commentsEnabled}
          onToggle={onToggleCommenting}
        />
        <OwnerMenuSwitchItem label="Chat dialog" checked={chatEnabled} onToggle={onToggleChat} />
        <OwnerMenuSwitchItem
          label="Participant dialog"
          checked={participantsEnabled}
          onToggle={onToggleParticipants}
        />
        <OwnerMenuSwitchItem label="GIFs" checked={giphyEnabled} onToggle={onToggleGifs} />
      </Collapse>
      <MenuItem onClick={() => toggleOwnerSection('visibility')}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ width: '100%' }}
        >
          <span>Visibility</span>
          {expandedOwnerSections.visibility ? (
            <ExpandLessIcon fontSize="small" />
          ) : (
            <ExpandMoreIcon fontSize="small" />
          )}
        </Stack>
      </MenuItem>
      <Collapse in={expandedOwnerSections.visibility} timeout="auto" unmountOnExit>
        <MenuItem
          sx={{ pl: 4 }}
          onClick={() => setIsCardCreatorNamesExpanded((currentExpanded) => !currentExpanded)}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ width: '100%' }}
          >
            <span>{cardCreatorNamesLabel}</span>
            {isCardCreatorNamesExpanded ? (
              <ExpandLessIcon fontSize="small" />
            ) : (
              <ExpandMoreIcon fontSize="small" />
            )}
          </Stack>
        </MenuItem>
        <Collapse in={isCardCreatorNamesExpanded} timeout="auto" unmountOnExit>
          <MenuItem
            sx={{ pl: 5 }}
            selected={participantNameVisibilityMode === 'hidden'}
            onClick={() => {
              onSetParticipantNameVisibility('hidden');
              closeMenu();
            }}
          >
            Hide card creator names
          </MenuItem>
          <MenuItem
            sx={{ pl: 5 }}
            selected={participantNameVisibilityMode === 'owner-only'}
            onClick={() => {
              onSetParticipantNameVisibility('owner-only');
              closeMenu();
            }}
          >
            Only I can see card creator names
          </MenuItem>
          <MenuItem
            sx={{ pl: 5 }}
            selected={participantNameVisibilityMode === 'everyone'}
            onClick={() => {
              onSetParticipantNameVisibility('everyone');
              closeMenu();
            }}
          >
            Everyone can see card creator names
          </MenuItem>
        </Collapse>
        {isDotVotingStyle && (
          <>
            <OwnerMenuSwitchItem
              label="Vote owners"
              checked={showVoterNames}
              onToggle={onToggleShowVoteOwners}
            />
            <OwnerMenuSwitchItem
              label="Private voting"
              checked={privateVotingEnabled}
              onToggle={onTogglePrivateVoting}
            />
          </>
        )}
      </Collapse>
      <MenuItem onClick={() => toggleOwnerSection('board')}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ width: '100%' }}
        >
          <span>Board</span>
          {expandedOwnerSections.board ? (
            <ExpandLessIcon fontSize="small" />
          ) : (
            <ExpandMoreIcon fontSize="small" />
          )}
        </Stack>
      </MenuItem>
      <Collapse in={expandedOwnerSections.board} timeout="auto" unmountOnExit>
        <OwnerMenuSwitchItem label="Board locked" checked={isLocked} onToggle={onToggleLockBoard} />
        <MenuItem
          sx={{ pl: 4 }}
          onClick={() => {
            onOpenSettings();
            closeMenu();
          }}
        >
          Edit Board
        </MenuItem>
        <MenuItem
          sx={{ pl: 4 }}
          onClick={() => {
            onOpenSetBackground();
            closeMenu();
          }}
        >
          Set Background
        </MenuItem>
        <Tooltip
          title={canSaveTemplates ? '' : 'Available with a gold subscription'}
          placement="right"
        >
          <span style={{ display: 'block' }}>
            <MenuItem
              sx={{ pl: 4 }}
              disabled={!canSaveTemplates}
              onClick={() => {
                if (!canSaveTemplates) {
                  return;
                }

                closeMenu();
                onOpenSaveTemplate();
              }}
            >
              Save as Template
            </MenuItem>
          </span>
        </Tooltip>
      </Collapse>
      <MenuItem onClick={() => toggleOwnerSection('cards')}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ width: '100%' }}
        >
          <span>Cards</span>
          {expandedOwnerSections.cards ? (
            <ExpandLessIcon fontSize="small" />
          ) : (
            <ExpandMoreIcon fontSize="small" />
          )}
        </Stack>
      </MenuItem>
      <Collapse in={expandedOwnerSections.cards} timeout="auto" unmountOnExit>
        <MenuItem
          sx={{ pl: 4 }}
          onClick={() => {
            closeMenu();
            onOpenImportCards();
          }}
        >
          Import Cards
        </MenuItem>
        <MenuItem
          sx={{ pl: 4 }}
          onClick={() => {
            closeMenu();
            void handleExportBoard();
          }}
          disabled={isExportingBoard}
        >
          Export Spreadsheet
        </MenuItem>
        <MenuItem
          sx={{ pl: 4 }}
          onClick={() => {
            closeMenu();
            onOpenClearBoard();
          }}
        >
          Clear Board
        </MenuItem>
      </Collapse>
      <MenuItem onClick={() => toggleOwnerSection('account')}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ width: '100%' }}
        >
          <span>Account</span>
          {expandedOwnerSections.account ? (
            <ExpandLessIcon fontSize="small" />
          ) : (
            <ExpandMoreIcon fontSize="small" />
          )}
        </Stack>
      </MenuItem>
      <Collapse in={expandedOwnerSections.account} timeout="auto" unmountOnExit>
        <MenuItem
          sx={{ pl: 4 }}
          onClick={() => {
            closeMenu();
            onOpenFeedback();
          }}
        >
          Help &amp; Feedback
        </MenuItem>
        <MenuItem
          sx={{ pl: 4 }}
          onClick={() => {
            closeMenu();
            onLogout();
          }}
        >
          Log Out
        </MenuItem>
      </Collapse>
    </Menu>
  );
};

export default BoardOwnerMenu;
